import React, { useState, useMemo } from 'react';
import { DESIGN } from '../components/DesignSystem';
import { 
  User, Shield, CreditCard, Building2, Users, Lock, Bell, 
  Mail, Smartphone, Puzzle, Database, Key, FileText, Palette,
  Globe, Layers, Activity, Settings as SettingsIcon, Search,
  ChevronRight, Check, X, Upload, Eye, EyeOff, Copy, RefreshCw,
  Download, Trash2, ExternalLink, AlertTriangle, Sparkles, Zap,
  Calendar, FileBox, PenTool, DollarSign, UserCircle, Megaphone,
  Code, MessageSquare, Workflow, BarChart3, FileSignature, Bot,
  ShieldCheck, ShoppingCart, Ticket, Import, Clock, Moon, Sun, Monitor,
  Plus, Edit3, ChevronDown, Filter, MoreHorizontal, Crown, CheckCircle2,
  ArrowUpRight, ArrowRight, Info, HelpCircle, CircleDot
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';

// ================================================
// SETTINGS NAVIGATION CATEGORIES
// ================================================
const SETTINGS_CATEGORIES = {
  account: {
    label: 'Account',
    items: [
      { id: 'profile', label: 'Profile', icon: User, description: 'Manage your account details' },
      { id: 'security', label: 'Security', icon: Shield, description: 'Password and 2FA settings' },
      { id: 'billing', label: 'Billing & Subscription', icon: CreditCard, description: 'Plans and payment' }
    ]
  },
  workspace: {
    label: 'Workspace',
    items: [
      { id: 'general', label: 'General', icon: Building2, description: 'Workspace configuration' },
      { id: 'team', label: 'Team Management', icon: Users, description: 'Manage team members' },
      { id: 'roles', label: 'Roles & Permissions', icon: Lock, description: 'Access control settings' },
      { id: 'lanes', label: 'Cognitive Lanes', icon: Layers, description: 'Lane configuration' }
    ]
  },
  notifications: {
    label: 'Notifications',
    items: [
      { id: 'inapp', label: 'In-App Notifications', icon: Bell, description: 'Configure alerts' },
      { id: 'email', label: 'Email Notifications', icon: Mail, description: 'Email preferences' },
      { id: 'push', label: 'Push Notifications', icon: Smartphone, description: 'Mobile alerts' }
    ]
  },
  integrations: {
    label: 'Integrations',
    items: [
      { id: 'integrations', label: 'Connected Apps', icon: Puzzle, description: 'Manage integrations' },
      { id: 'api', label: 'API Access', icon: Key, description: 'API keys and usage' },
      { id: 'webhooks', label: 'Webhooks', icon: Zap, description: 'Event automation' }
    ]
  },
  data: {
    label: 'Data',
    items: [
      { id: 'privacy', label: 'Data & Privacy', icon: Database, description: 'Privacy controls' },
      { id: 'audit', label: 'Audit Log', icon: FileText, description: 'Activity history' }
    ]
  },
  customization: {
    label: 'Customization',
    items: [
      { id: 'appearance', label: 'Appearance', icon: Palette, description: 'Theme and colors' },
      { id: 'dashboard', label: 'Dashboard Layout', icon: Layers, description: 'Customize views' },
      { id: 'language', label: 'Language & Region', icon: Globe, description: 'Localization' }
    ]
  },
  framework: {
    label: 'Cognitive Framework',
    items: [
      { id: 'laneconfig', label: 'Lane Configuration', icon: Layers, description: 'Customize cognitive lanes' },
      { id: 'levels', label: 'Level Definitions', icon: Activity, description: 'Define level criteria' },
      { id: 'thresholds', label: 'Load Thresholds', icon: AlertTriangle, description: 'Alert thresholds' },
      { id: 'visibility', label: 'Framework Visibility', icon: Eye, description: 'Data access controls' }
    ]
  }
};

// ================================================
// INTEGRATIONS DATA - COMPREHENSIVE
// ================================================
const INTEGRATIONS = {
  migration: [
    { id: 'asana', name: 'Asana', logo: '🔵', description: 'Import projects, tasks, and team structure from Asana', connected: false, category: 'Project Management' },
    { id: 'monday', name: 'Monday.com', logo: '🟣', description: 'Migrate boards and workflows seamlessly', connected: false, category: 'Project Management' },
    { id: 'clickup', name: 'ClickUp', logo: '🟢', description: 'Import spaces, folders, and tasks hierarchy', connected: false, category: 'Project Management' },
    { id: 'slack', name: 'Slack', logo: '💬', description: 'Import channels and message history', connected: false, category: 'Communication' },
    { id: 'notion', name: 'Notion', logo: '⬛', description: 'Import pages and databases', connected: false, category: 'Documentation' },
    { id: 'trello', name: 'Trello', logo: '🔷', description: 'Import boards and cards', connected: false, category: 'Project Management' },
    { id: 'basecamp', name: 'Basecamp', logo: '🏕️', description: 'Import projects and to-dos', connected: false, category: 'Project Management' }
  ],
  calendar: [
    { id: 'gcal', name: 'Google Calendar', logo: '📅', description: 'Sync events, meetings, and deadlines', lane: 'Operations', connected: true, lastSync: '5 min ago' },
    { id: 'outlook_cal', name: 'Outlook Calendar', logo: '📆', description: 'Microsoft calendar integration', lane: 'Operations', connected: false },
    { id: 'apple_cal', name: 'Apple Calendar', logo: '🍎', description: 'iCloud calendar sync', lane: 'Operations', connected: false },
    { id: 'calendly', name: 'Calendly', logo: '📋', description: 'Scheduling and availability management', lane: 'Relationships', connected: true, lastSync: '1 hour ago' },
    { id: 'calcom', name: 'Cal.com', logo: '⏰', description: 'Open-source scheduling platform', lane: 'Relationships', connected: false }
  ],
  email: [
    { id: 'gmail', name: 'Gmail', logo: '✉️', description: 'Email sync and thread tracking', lane: 'Communication', connected: true, lastSync: '2 min ago' },
    { id: 'outlook_mail', name: 'Outlook Mail', logo: '📧', description: 'Microsoft email integration', lane: 'Communication', connected: false },
    { id: 'apple_mail', name: 'Apple Mail', logo: '💌', description: 'iCloud email sync', lane: 'Communication', connected: false }
  ],
  files: [
    { id: 'gdrive', name: 'Google Drive', logo: '📁', description: 'File storage and collaboration', lane: 'Execution', connected: true, lastSync: '10 min ago' },
    { id: 'dropbox', name: 'Dropbox', logo: '📦', description: 'Cloud file management', lane: 'Execution', connected: false },
    { id: 'onedrive', name: 'OneDrive', logo: '☁️', description: 'Microsoft cloud storage', lane: 'Execution', connected: false },
    { id: 'box', name: 'Box', logo: '📥', description: 'Enterprise file sharing', lane: 'Execution', connected: false },
    { id: 'icloud', name: 'iCloud Drive', logo: '🍏', description: 'Apple cloud storage', lane: 'Execution', connected: false }
  ],
  design: [
    { id: 'figma', name: 'Figma', logo: '🎨', description: 'Design file sync and handoff', lane: 'Creative', connected: true, lastSync: '30 min ago' },
    { id: 'adobe', name: 'Adobe Creative Cloud', logo: '🅰️', description: 'Creative suite integration', lane: 'Creative', connected: false },
    { id: 'canva', name: 'Canva', logo: '🖼️', description: 'Design templates and assets', lane: 'Creative', connected: false },
    { id: 'miro', name: 'Miro', logo: '📝', description: 'Whiteboard collaboration', lane: 'Creative', connected: false },
    { id: 'frameio', name: 'Frame.io', logo: '🎬', description: 'Video review workflows', lane: 'Creative', connected: false },
    { id: 'invision', name: 'InVision', logo: '💡', description: 'Prototyping and feedback', lane: 'Creative', connected: false }
  ],
  finance: [
    { id: 'quickbooks', name: 'QuickBooks', logo: '💵', description: 'Accounting and invoicing', lane: 'Finance & Analysis', connected: false },
    { id: 'xero', name: 'Xero', logo: '💰', description: 'Financial management', lane: 'Finance & Analysis', connected: false },
    { id: 'freshbooks', name: 'FreshBooks', logo: '📊', description: 'Time tracking and billing', lane: 'Finance & Analysis', connected: false },
    { id: 'wave', name: 'Wave', logo: '🌊', description: 'Free accounting software', lane: 'Finance & Analysis', connected: false },
    { id: 'stripe', name: 'Stripe', logo: '💳', description: 'Payment processing', lane: 'Revenue', connected: true, lastSync: '1 hour ago' },
    { id: 'paypal', name: 'PayPal Business', logo: '🅿️', description: 'Payment integration', lane: 'Revenue', connected: false },
    { id: 'plaid', name: 'Plaid', logo: '🏦', description: 'Bank account linking', lane: 'Finance & Analysis', connected: false },
    { id: 'square', name: 'Square', logo: '⬜', description: 'Point of sale integration', lane: 'Revenue', connected: false }
  ],
  hr: [
    { id: 'gusto', name: 'Gusto', logo: '👥', description: 'Payroll and benefits', lane: 'Operations', connected: false },
    { id: 'bamboohr', name: 'BambooHR', logo: '🎋', description: 'HR management platform', lane: 'Operations', connected: false },
    { id: 'rippling', name: 'Rippling', logo: '🌀', description: 'Employee management', lane: 'Operations', connected: false },
    { id: 'deel', name: 'Deel', logo: '🌍', description: 'Global payroll and compliance', lane: 'Operations', connected: false }
  ],
  marketing: [
    { id: 'ga', name: 'Google Analytics', logo: '📈', description: 'Website analytics and insights', lane: 'Marketing', connected: true, lastSync: '15 min ago' },
    { id: 'mailchimp', name: 'Mailchimp', logo: '🐵', description: 'Email marketing campaigns', lane: 'Marketing', connected: false },
    { id: 'hubspot', name: 'HubSpot', logo: '🟠', description: 'CRM and marketing automation', lane: 'Marketing', connected: true, lastSync: '20 min ago' },
    { id: 'salesforce', name: 'Salesforce', logo: '☁️', description: 'CRM and sales pipeline', lane: 'Relationships', connected: false },
    { id: 'klaviyo', name: 'Klaviyo', logo: '📬', description: 'E-commerce email marketing', lane: 'Marketing', connected: false },
    { id: 'semrush', name: 'Semrush', logo: '🔍', description: 'SEO and competitive analysis', lane: 'Marketing', connected: false },
    { id: 'ahrefs', name: 'Ahrefs', logo: '🔗', description: 'Backlink and SEO tools', lane: 'Marketing', connected: false },
    { id: 'hootsuite', name: 'Hootsuite', logo: '🦉', description: 'Social media management', lane: 'Marketing', connected: false },
    { id: 'buffer', name: 'Buffer', logo: '📱', description: 'Social media scheduling', lane: 'Marketing', connected: false },
    { id: 'sprout', name: 'Sprout Social', logo: '🌱', description: 'Social media analytics', lane: 'Marketing', connected: false },
    { id: 'later', name: 'Later', logo: '⏳', description: 'Visual social planning', lane: 'Marketing', connected: false },
    { id: 'meta', name: 'Meta Business Suite', logo: '📘', description: 'Facebook and Instagram', lane: 'Marketing', connected: false },
    { id: 'linkedin', name: 'LinkedIn', logo: '💼', description: 'Professional networking', lane: 'Marketing', connected: false },
    { id: 'twitter', name: 'Twitter/X', logo: '🐦', description: 'Social media integration', lane: 'Marketing', connected: false }
  ],
  development: [
    { id: 'github', name: 'GitHub', logo: '🐙', description: 'Code repository and issues', lane: 'Execution', connected: true, lastSync: '5 min ago' },
    { id: 'gitlab', name: 'GitLab', logo: '🦊', description: 'DevOps platform', lane: 'Execution', connected: false },
    { id: 'sentry', name: 'Sentry', logo: '🐛', description: 'Error tracking and monitoring', lane: 'Execution', connected: false },
    { id: 'vercel', name: 'Vercel', logo: '▲', description: 'Deployment platform', lane: 'Execution', connected: false },
    { id: 'aws', name: 'AWS', logo: '🔶', description: 'Cloud infrastructure', lane: 'Execution', connected: false },
    { id: 'datadog', name: 'Datadog', logo: '🐕', description: 'Monitoring and analytics', lane: 'Execution', connected: false }
  ],
  communication: [
    { id: 'loom', name: 'Loom', logo: '🎥', description: 'Video messaging', lane: 'Communication', connected: false },
    { id: 'intercom', name: 'Intercom', logo: '💬', description: 'Customer messaging', lane: 'Relationships', connected: false },
    { id: 'zoom', name: 'Zoom', logo: '📹', description: 'Video conferencing', lane: 'Communication', connected: true, lastSync: '1 hour ago' },
    { id: 'meet', name: 'Google Meet', logo: '🎦', description: 'Video meetings', lane: 'Communication', connected: false },
    { id: 'teams', name: 'Microsoft Teams', logo: '👥', description: 'Team collaboration', lane: 'Communication', connected: false }
  ],
  automation: [
    { id: 'zapier', name: 'Zapier', logo: '⚡', description: 'Workflow automation', lane: 'Operations', connected: true, lastSync: '10 min ago' },
    { id: 'make', name: 'Make', logo: '🔧', description: 'Advanced automations', lane: 'Operations', connected: false },
    { id: 'n8n', name: 'n8n', logo: '🔄', description: 'Self-hosted automation', lane: 'Operations', connected: false },
    { id: 'pabbly', name: 'Pabbly', logo: '🔗', description: 'Business automation', lane: 'Operations', connected: false }
  ],
  data: [
    { id: 'sheets', name: 'Google Sheets', logo: '📊', description: 'Spreadsheet integration', lane: 'Finance & Analysis', connected: true, lastSync: '30 min ago' },
    { id: 'airtable', name: 'Airtable', logo: '📋', description: 'Database and spreadsheets', lane: 'Finance & Analysis', connected: false },
    { id: 'tableau', name: 'Tableau', logo: '📉', description: 'Data visualization', lane: 'Finance & Analysis', connected: false },
    { id: 'looker', name: 'Looker', logo: '👁️', description: 'Business intelligence', lane: 'Finance & Analysis', connected: false },
    { id: 'powerbi', name: 'Power BI', logo: '📈', description: 'Microsoft analytics', lane: 'Finance & Analysis', connected: false }
  ],
  contracts: [
    { id: 'docusign', name: 'DocuSign', logo: '✍️', description: 'E-signatures and contracts', lane: 'Relationships', connected: false },
    { id: 'hellosign', name: 'HelloSign', logo: '👋', description: 'Digital signatures', lane: 'Relationships', connected: false },
    { id: 'pandadoc', name: 'PandaDoc', logo: '🐼', description: 'Document automation', lane: 'Relationships', connected: false },
    { id: 'bonsai', name: 'Bonsai', logo: '🌳', description: 'Freelance contracts', lane: 'Relationships', connected: false },
    { id: 'honeybook', name: 'HoneyBook', logo: '🍯', description: 'Client management', lane: 'Relationships', connected: false }
  ],
  ai: [
    { id: 'openai', name: 'OpenAI API', logo: '🤖', description: 'GPT models and AI features', lane: 'Execution', connected: true, lastSync: 'Active' },
    { id: 'anthropic', name: 'Anthropic API', logo: '🧠', description: 'Claude AI integration', lane: 'Execution', connected: false },
    { id: 'grammarly', name: 'Grammarly', logo: '✏️', description: 'Writing assistance', lane: 'Creative', connected: false },
    { id: 'otter', name: 'Otter.ai', logo: '🦦', description: 'Meeting transcription', lane: 'Communication', connected: false },
    { id: 'rev', name: 'Rev.ai', logo: '🎙️', description: 'Speech recognition', lane: 'Communication', connected: false }
  ],
  security: [
    { id: 'google_sso', name: 'Google SSO', logo: '🔐', description: 'Single sign-on with Google', lane: 'Operations', connected: true, lastSync: 'Active' },
    { id: 'microsoft_sso', name: 'Microsoft SSO', logo: '🔑', description: 'Azure AD integration', lane: 'Operations', connected: false },
    { id: 'okta', name: 'Okta', logo: '🛡️', description: 'Identity management', lane: 'Operations', connected: false },
    { id: 'auth0', name: 'Auth0', logo: '🔒', description: 'Authentication platform', lane: 'Operations', connected: false },
    { id: 'onelogin', name: 'OneLogin', logo: '1️⃣', description: 'Cloud identity', lane: 'Operations', connected: false },
    { id: 'lastpass', name: 'LastPass Business', logo: '🔏', description: 'Password management', lane: 'Operations', connected: false },
    { id: '1password', name: '1Password Business', logo: '🗝️', description: 'Team password vault', lane: 'Operations', connected: false }
  ],
  ecommerce: [
    { id: 'shopify', name: 'Shopify', logo: '🛒', description: 'E-commerce platform', lane: 'Revenue', connected: false },
    { id: 'woocommerce', name: 'WooCommerce', logo: '🛍️', description: 'WordPress e-commerce', lane: 'Revenue', connected: false }
  ],
  events: [
    { id: 'eventbrite', name: 'Eventbrite', logo: '🎟️', description: 'Event management', lane: 'Marketing', connected: false },
    { id: 'zoom_webinar', name: 'Zoom Webinars', logo: '📺', description: 'Webinar hosting', lane: 'Marketing', connected: false },
    { id: 'hopin', name: 'Hopin', logo: '🎪', description: 'Virtual events', lane: 'Marketing', connected: false }
  ]
};

const INTEGRATION_CATEGORIES = [
  { id: 'all', label: 'All', icon: Puzzle },
  { id: 'calendar', label: 'Calendar', icon: Calendar },
  { id: 'email', label: 'Email', icon: Mail },
  { id: 'files', label: 'Files', icon: FileBox },
  { id: 'design', label: 'Design', icon: PenTool },
  { id: 'finance', label: 'Finance', icon: DollarSign },
  { id: 'hr', label: 'HR', icon: UserCircle },
  { id: 'marketing', label: 'Marketing', icon: Megaphone },
  { id: 'development', label: 'Development', icon: Code },
  { id: 'communication', label: 'Communication', icon: MessageSquare },
  { id: 'automation', label: 'Automation', icon: Workflow },
  { id: 'data', label: 'Analytics', icon: BarChart3 },
  { id: 'contracts', label: 'CRM', icon: FileSignature },
  { id: 'ai', label: 'AI', icon: Bot },
  { id: 'security', label: 'Security', icon: ShieldCheck },
  { id: 'ecommerce', label: 'E-commerce', icon: ShoppingCart },
  { id: 'events', label: 'Events', icon: Ticket }
];

// ================================================
// BILLING PLANS
// ================================================
const BILLING_PLANS = [
  { id: 'starter', name: 'Starter', price: 99, users: 10, color: '#10B981', features: ['Up to 10 team members', 'Core cognitive framework', 'Basic integrations', 'Email support', 'Standard analytics'] },
  { id: 'growth', name: 'Growth', price: 199, users: 20, color: '#3B82F6', features: ['Up to 20 team members', 'Advanced analytics', 'Priority integrations', 'Priority support', 'Custom lanes'] },
  { id: 'scale', name: 'Scale', price: 350, users: 40, color: '#8B5CF6', popular: true, features: ['Up to 40 team members', 'Custom cognitive lanes', 'All integrations', 'Dedicated support', 'API access'] },
  { id: 'enterprise', name: 'Enterprise', price: 499, users: 60, color: '#F59E0B', features: ['Up to 60 team members', 'White-label options', 'Full API access', 'SLA guarantee', 'Custom onboarding'] }
];

// ================================================
// COGNITIVE LANES CONFIG
// ================================================
const COGNITIVE_LANES = [
  { id: 'vision', name: 'Vision & Expansion', color: '#38BDF8', icon: '🔭', description: 'Strategic growth and future planning' },
  { id: 'strategy', name: 'Strategy & Architecture', color: '#1E3A5F', icon: '🏗️', description: 'System design and strategic decisions' },
  { id: 'creative', name: 'Creative', color: '#A855F7', icon: '🎨', description: 'Design, content, and creative direction' },
  { id: 'marketing', name: 'Marketing', color: '#FB923C', icon: '📣', description: 'Brand, campaigns, and market presence' },
  { id: 'operations', name: 'Operations', color: '#22C55E', icon: '⚙️', description: 'Day-to-day processes and efficiency' },
  { id: 'execution', name: 'Execution', color: '#14B8A6', icon: '🎯', description: 'Task completion and delivery' },
  { id: 'revenue', name: 'Revenue', color: '#EAB308', icon: '💰', description: 'Sales, deals, and revenue generation' },
  { id: 'relationships', name: 'Relationships', color: '#F472B6', icon: '🤝', description: 'Client and partner management' },
  { id: 'culture', name: 'Culture', color: '#C4B5FD', icon: '🌟', description: 'Team culture and engagement' },
  { id: 'finance', name: 'Finance & Analysis', color: '#64748B', icon: '📊', description: 'Financial planning and analysis' }
];

// ================================================
// TEAM MEMBERS MOCK DATA
// ================================================
const TEAM_MEMBERS = [
  { id: 1, name: 'Sarah Chen', email: 'sarah@company.com', avatar: 'SC', role: 'CEO', level: 'L4', lane: 'strategy', status: 'active', joinDate: '2023-01-15' },
  { id: 2, name: 'Michael Brooks', email: 'michael@company.com', avatar: 'MB', role: 'CTO', level: 'L4', lane: 'execution', status: 'active', joinDate: '2023-02-20' },
  { id: 3, name: 'Emily Rodriguez', email: 'emily@company.com', avatar: 'ER', role: 'Creative Director', level: 'L3', lane: 'creative', status: 'active', joinDate: '2023-03-10' },
  { id: 4, name: 'James Wilson', email: 'james@company.com', avatar: 'JW', role: 'Marketing Manager', level: 'L3', lane: 'marketing', status: 'active', joinDate: '2023-04-05' },
  { id: 5, name: 'Lisa Park', email: 'lisa@company.com', avatar: 'LP', role: 'Operations Lead', level: 'L3', lane: 'operations', status: 'active', joinDate: '2023-05-12' },
  { id: 6, name: 'David Kim', email: 'david@company.com', avatar: 'DK', role: 'Senior Developer', level: 'L2', lane: 'execution', status: 'active', joinDate: '2023-06-01' },
  { id: 7, name: 'Anna Martinez', email: 'anna@company.com', avatar: 'AM', role: 'Designer', level: 'L2', lane: 'creative', status: 'active', joinDate: '2023-07-20' },
  { id: 8, name: 'Chris Taylor', email: 'chris@company.com', avatar: 'CT', role: 'Sales Rep', level: 'L2', lane: 'revenue', status: 'away', joinDate: '2023-08-15' }
];

// ================================================
// AUDIT LOG DATA
// ================================================
const AUDIT_LOGS = [
  { id: 1, user: 'Sarah Chen', action: 'Updated billing plan to Scale', type: 'billing', timestamp: '2 hours ago', ip: '192.168.1.1' },
  { id: 2, user: 'Michael Brooks', action: 'Added new integration: GitHub', type: 'integration', timestamp: '5 hours ago', ip: '192.168.1.2' },
  { id: 3, user: 'Emily Rodriguez', action: 'Created new project: Q1 Campaign', type: 'project', timestamp: '1 day ago', ip: '192.168.1.3' },
  { id: 4, user: 'James Wilson', action: 'Invited team member: Anna Martinez', type: 'team', timestamp: '2 days ago', ip: '192.168.1.4' },
  { id: 5, user: 'Lisa Park', action: 'Modified lane configuration', type: 'settings', timestamp: '3 days ago', ip: '192.168.1.5' },
  { id: 6, user: 'Sarah Chen', action: 'Exported organization data', type: 'data', timestamp: '4 days ago', ip: '192.168.1.1' },
  { id: 7, user: 'David Kim', action: 'Regenerated API key', type: 'security', timestamp: '5 days ago', ip: '192.168.1.6' },
  { id: 8, user: 'Chris Taylor', action: 'Updated notification preferences', type: 'settings', timestamp: '1 week ago', ip: '192.168.1.7' }
];

// ================================================
// COMPONENT: Settings Navigation Sidebar
// ================================================
const SettingsNav = ({ activeSection, onSelect }) => (
  <div 
    className="w-72 flex-shrink-0 rounded-2xl overflow-hidden"
    style={{ 
      backgroundColor: '#0F172A',
      boxShadow: DESIGN.shadows.xl
    }}
    data-testid="settings-nav"
  >
    <div className="p-5 border-b" style={{ borderColor: 'rgba(255,255,255,0.08)' }}>
      <div className="flex items-center gap-4">
        <div 
          className="w-12 h-12 rounded-xl flex items-center justify-center"
          style={{ 
            background: `linear-gradient(135deg, ${DESIGN.colors.primary} 0%, ${DESIGN.colors.primaryDark} 100%)`,
            boxShadow: DESIGN.shadows.glowPrimary
          }}
        >
          <SettingsIcon className="w-6 h-6 text-white" />
        </div>
        <div>
          <h2 className="text-lg font-bold text-white">Settings</h2>
          <p className="text-xs text-slate-400">Configure your workspace</p>
        </div>
      </div>
    </div>
    
    <div className="p-4 space-y-5 max-h-[calc(100vh-280px)] overflow-y-auto settings-scroll">
      {Object.entries(SETTINGS_CATEGORIES).map(([groupKey, group]) => (
        <div key={groupKey}>
          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 px-3 mb-2.5">
            {group.label}
          </p>
          <div className="space-y-1">
            {group.items.map(item => {
              const isActive = activeSection === item.id;
              const Icon = item.icon;
              return (
                <button
                  key={item.id}
                  onClick={() => onSelect(item.id)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
                    isActive ? 'text-white' : 'text-slate-400 hover:text-white hover:bg-white/5'
                  }`}
                  style={isActive ? {
                    backgroundColor: `${DESIGN.colors.primary}20`,
                    borderLeft: `3px solid ${DESIGN.colors.primary}`,
                    boxShadow: `inset 0 0 30px ${DESIGN.colors.primary}15, 0 0 20px ${DESIGN.colors.primary}20`
                  } : { borderLeft: '3px solid transparent' }}
                  data-testid={`settings-nav-${item.id}`}
                >
                  <Icon className="w-4 h-4" style={{ color: isActive ? DESIGN.colors.primary : undefined }} />
                  <span className="truncate">{item.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  </div>
);

// ================================================
// COMPONENT: Form Input
// ================================================
const FormInput = ({ label, type = 'text', value, onChange, placeholder, disabled, helper, rightElement }) => (
  <div className="space-y-2">
    <label className="text-sm font-semibold" style={{ color: DESIGN.colors.textPrimary }}>{label}</label>
    <div className="relative">
      <input
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        disabled={disabled}
        className="w-full px-4 py-3 rounded-xl text-sm transition-all outline-none focus:ring-2"
        style={{ 
          backgroundColor: DESIGN.colors.background,
          border: `1px solid ${DESIGN.colors.border}`,
          color: DESIGN.colors.textPrimary,
          '--tw-ring-color': DESIGN.colors.primary
        }}
      />
      {rightElement && (
        <div className="absolute right-3 top-1/2 -translate-y-1/2">
          {rightElement}
        </div>
      )}
    </div>
    {helper && <p className="text-xs" style={{ color: DESIGN.colors.textMuted }}>{helper}</p>}
  </div>
);

// ================================================
// COMPONENT: Premium Toggle Switch
// ================================================
const PremiumToggle = ({ label, description, checked, onChange }) => (
  <div className="flex items-center justify-between py-4 border-b" style={{ borderColor: DESIGN.colors.borderSubtle }}>
    <div className="flex-1 pr-4">
      <p className="text-sm font-semibold" style={{ color: DESIGN.colors.textPrimary }}>{label}</p>
      {description && <p className="text-xs mt-0.5" style={{ color: DESIGN.colors.textMuted }}>{description}</p>}
    </div>
    <button
      onClick={() => onChange(!checked)}
      className={`relative w-14 h-7 rounded-full transition-all duration-300 ${checked ? '' : 'bg-gray-200'}`}
      style={checked ? { 
        background: `linear-gradient(135deg, ${DESIGN.colors.primary} 0%, ${DESIGN.colors.primaryDark} 100%)`,
        boxShadow: `0 0 20px ${DESIGN.colors.primary}50`
      } : {}}
    >
      <div 
        className={`absolute top-1 w-5 h-5 rounded-full bg-white shadow-md transition-all duration-300 ${
          checked ? 'left-8' : 'left-1'
        }`}
      />
    </button>
  </div>
);

// ================================================
// COMPONENT: Section Card
// ================================================
const SectionCard = ({ title, description, children, headerAction, className = '' }) => (
  <div 
    className={`rounded-2xl overflow-hidden ${className}`}
    style={{ 
      backgroundColor: DESIGN.colors.card,
      boxShadow: DESIGN.shadows.lg
    }}
  >
    {title && (
      <div className="px-6 py-5 border-b flex items-center justify-between" style={{ borderColor: DESIGN.colors.border }}>
        <div>
          <h3 className="text-lg font-bold" style={{ color: DESIGN.colors.textPrimary }}>{title}</h3>
          {description && <p className="text-sm mt-0.5" style={{ color: DESIGN.colors.textMuted }}>{description}</p>}
        </div>
        {headerAction}
      </div>
    )}
    <div className="p-6">
      {children}
    </div>
  </div>
);

// ================================================
// COMPONENT: Premium Integration Card
// ================================================
const IntegrationCard = ({ integration, isImport, onConnect }) => (
  <div 
    className="rounded-2xl p-5 transition-all hover:shadow-xl cursor-pointer group border"
    style={{ 
      backgroundColor: DESIGN.colors.card,
      borderColor: integration.connected ? `${DESIGN.colors.primary}40` : DESIGN.colors.border
    }}
    onClick={() => onConnect(integration)}
    data-testid={`integration-card-${integration.id}`}
  >
    <div className="flex items-start gap-4">
      <div 
        className="w-14 h-14 rounded-xl flex items-center justify-center text-2xl flex-shrink-0"
        style={{ backgroundColor: DESIGN.colors.background }}
      >
        {integration.logo}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <h4 className="font-bold text-base" style={{ color: DESIGN.colors.textPrimary }}>{integration.name}</h4>
          {isImport && (
            <span 
              className="text-[10px] font-black px-2.5 py-1 rounded-lg uppercase"
              style={{ background: 'linear-gradient(135deg, #8B5CF6 0%, #A855F7 100%)', color: 'white' }}
            >
              Import
            </span>
          )}
        </div>
        <p className="text-sm mt-1 line-clamp-2" style={{ color: DESIGN.colors.textMuted }}>
          {integration.description}
        </p>
        {integration.lane && (
          <p className="text-xs mt-2 font-semibold flex items-center gap-1.5" style={{ color: DESIGN.colors.primary }}>
            <Layers className="w-3 h-3" />
            {integration.lane} Lane
          </p>
        )}
      </div>
    </div>
    <div className="flex items-center justify-between mt-4 pt-4 border-t" style={{ borderColor: DESIGN.colors.border }}>
      {integration.connected ? (
        <div className="flex items-center gap-2">
          <div className="w-2.5 h-2.5 rounded-full animate-pulse" style={{ backgroundColor: DESIGN.colors.success, boxShadow: `0 0 8px ${DESIGN.colors.success}` }} />
          <span className="text-xs font-bold" style={{ color: DESIGN.colors.success }}>Connected</span>
          {integration.lastSync && (
            <span className="text-xs" style={{ color: DESIGN.colors.textMuted }}>• {integration.lastSync}</span>
          )}
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: DESIGN.colors.textMuted }} />
          <span className="text-xs font-medium" style={{ color: DESIGN.colors.textMuted }}>Not connected</span>
        </div>
      )}
      <Button
        size="sm"
        className="rounded-xl text-xs font-bold opacity-0 group-hover:opacity-100 transition-opacity"
        style={{ 
          backgroundColor: integration.connected ? DESIGN.colors.background : DESIGN.colors.primary,
          color: integration.connected ? DESIGN.colors.textPrimary : 'white'
        }}
      >
        {integration.connected ? 'Manage' : isImport ? 'Import' : 'Connect'}
      </Button>
    </div>
  </div>
);

// ================================================
// COMPONENT: Save Button
// ================================================
const SaveButton = ({ onClick, loading }) => (
  <Button 
    onClick={onClick}
    disabled={loading}
    className="px-8 rounded-xl font-bold"
    style={{ 
      background: `linear-gradient(135deg, ${DESIGN.colors.primary} 0%, ${DESIGN.colors.primaryDark} 100%)`,
      boxShadow: DESIGN.shadows.glowPrimary
    }}
  >
    {loading ? 'Saving...' : 'Save Changes'}
  </Button>
);

// ================================================
// SETTINGS SECTIONS
// ================================================

// Profile Section
const ProfileSection = () => (
  <div className="space-y-6">
    <SectionCard title="Personal Information" description="Manage your account details and preferences">
      <div className="flex items-start gap-8 mb-8">
        <div className="relative group">
          <div 
            className="w-24 h-24 rounded-2xl flex items-center justify-center text-3xl font-bold cursor-pointer transition-all group-hover:scale-105"
            style={{ 
              background: `linear-gradient(135deg, ${DESIGN.colors.primary} 0%, ${DESIGN.colors.primaryDark} 100%)`,
              color: 'white',
              boxShadow: DESIGN.shadows.glowPrimary
            }}
          >
            DU
          </div>
          <button 
            className="absolute -bottom-2 -right-2 w-8 h-8 rounded-xl flex items-center justify-center transition-all hover:scale-110"
            style={{ backgroundColor: DESIGN.colors.card, boxShadow: DESIGN.shadows.lg }}
          >
            <Upload className="w-4 h-4" style={{ color: DESIGN.colors.primary }} />
          </button>
        </div>
        <div className="flex-1">
          <h4 className="text-xl font-bold" style={{ color: DESIGN.colors.textPrimary }}>Demo User</h4>
          <p className="text-sm" style={{ color: DESIGN.colors.textMuted }}>demo@cognitiveos.com</p>
          <p className="text-xs mt-2 flex items-center gap-1.5" style={{ color: DESIGN.colors.primary }}>
            <Crown className="w-3.5 h-3.5" />
            Account Owner
          </p>
          <Button variant="ghost" size="sm" className="mt-3 text-xs rounded-lg" style={{ color: DESIGN.colors.primary }}>
            Change avatar
          </Button>
        </div>
      </div>
      
      <div className="grid grid-cols-2 gap-5">
        <FormInput label="First Name" value="Demo" placeholder="Enter first name" />
        <FormInput label="Last Name" value="User" placeholder="Enter last name" />
        <FormInput label="Email" type="email" value="demo@cognitiveos.com" placeholder="Enter email" />
        <FormInput label="Phone" type="tel" value="" placeholder="Enter phone number" />
      </div>
    </SectionCard>
    
    <SectionCard title="Preferences">
      <div className="grid grid-cols-2 gap-5">
        <div className="space-y-2">
          <label className="text-sm font-semibold" style={{ color: DESIGN.colors.textPrimary }}>Timezone</label>
          <select 
            className="w-full px-4 py-3 rounded-xl text-sm appearance-none cursor-pointer"
            style={{ backgroundColor: DESIGN.colors.background, border: `1px solid ${DESIGN.colors.border}`, color: DESIGN.colors.textPrimary }}
          >
            <option>America/New_York (EST)</option>
            <option>America/Los_Angeles (PST)</option>
            <option>Europe/London (GMT)</option>
            <option>Asia/Tokyo (JST)</option>
          </select>
        </div>
        <div className="space-y-2">
          <label className="text-sm font-semibold" style={{ color: DESIGN.colors.textPrimary }}>Language</label>
          <select 
            className="w-full px-4 py-3 rounded-xl text-sm appearance-none cursor-pointer"
            style={{ backgroundColor: DESIGN.colors.background, border: `1px solid ${DESIGN.colors.border}`, color: DESIGN.colors.textPrimary }}
          >
            <option>English (US)</option>
            <option>English (UK)</option>
            <option>Spanish</option>
            <option>French</option>
            <option>German</option>
          </select>
        </div>
      </div>
    </SectionCard>
    
    <div className="flex justify-end">
      <SaveButton />
    </div>
  </div>
);

// Security Section
const SecuritySection = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(true);
  
  const sessions = [
    { device: 'MacBook Pro 16"', location: 'New York, US', current: true, time: 'Now', browser: 'Chrome 120' },
    { device: 'iPhone 15 Pro', location: 'New York, US', current: false, time: '2 hours ago', browser: 'Safari' },
    { device: 'Windows Desktop', location: 'Boston, US', current: false, time: '1 day ago', browser: 'Chrome 120' },
    { device: 'iPad Pro', location: 'Miami, US', current: false, time: '3 days ago', browser: 'Safari' }
  ];
  
  return (
    <div className="space-y-6">
      <SectionCard title="Password" description="Change your password to keep your account secure">
        <div className="space-y-4 max-w-lg">
          <FormInput 
            label="Current Password" 
            type={showPassword ? 'text' : 'password'} 
            value="" 
            placeholder="Enter current password"
            rightElement={
              <button onClick={() => setShowPassword(!showPassword)}>
                {showPassword ? <EyeOff className="w-4 h-4" style={{ color: DESIGN.colors.textMuted }} /> : <Eye className="w-4 h-4" style={{ color: DESIGN.colors.textMuted }} />}
              </button>
            }
          />
          <FormInput label="New Password" type="password" value="" placeholder="Enter new password" />
          <FormInput label="Confirm Password" type="password" value="" placeholder="Confirm new password" />
          <Button 
            className="rounded-xl"
            style={{ background: `linear-gradient(135deg, ${DESIGN.colors.primary} 0%, ${DESIGN.colors.primaryDark} 100%)` }}
          >
            Update Password
          </Button>
        </div>
      </SectionCard>
      
      <SectionCard title="Two-Factor Authentication" description="Add an extra layer of security to your account">
        <div 
          className="flex items-center justify-between p-5 rounded-xl"
          style={{ 
            backgroundColor: twoFactorEnabled ? `${DESIGN.colors.success}08` : DESIGN.colors.background,
            border: `2px solid ${twoFactorEnabled ? DESIGN.colors.success : DESIGN.colors.border}`
          }}
        >
          <div className="flex items-center gap-5">
            <div 
              className="w-14 h-14 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: `${DESIGN.colors.success}15` }}
            >
              <Shield className="w-7 h-7" style={{ color: DESIGN.colors.success }} />
            </div>
            <div>
              <p className="font-bold text-base" style={{ color: DESIGN.colors.textPrimary }}>
                {twoFactorEnabled ? '2FA is enabled' : '2FA is disabled'}
              </p>
              <p className="text-sm" style={{ color: DESIGN.colors.textMuted }}>
                {twoFactorEnabled ? 'Using authenticator app' : 'Enable for enhanced security'}
              </p>
            </div>
          </div>
          <Button 
            variant={twoFactorEnabled ? 'outline' : 'default'} 
            className="rounded-xl"
            style={!twoFactorEnabled ? { backgroundColor: DESIGN.colors.primary } : {}}
          >
            {twoFactorEnabled ? 'Manage' : 'Enable'}
          </Button>
        </div>
      </SectionCard>
      
      <SectionCard 
        title="Active Sessions" 
        description="Manage your active login sessions"
        headerAction={
          <Button variant="ghost" size="sm" className="text-xs rounded-lg" style={{ color: DESIGN.colors.error }}>
            Revoke All
          </Button>
        }
      >
        <div className="space-y-3">
          {sessions.map((session, idx) => (
            <div 
              key={idx} 
              className="flex items-center justify-between p-4 rounded-xl transition-all hover:shadow-md"
              style={{ backgroundColor: DESIGN.colors.background }}
            >
              <div className="flex items-center gap-4">
                <div 
                  className="w-10 h-10 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: session.current ? `${DESIGN.colors.primary}15` : `${DESIGN.colors.textMuted}10` }}
                >
                  <Monitor className="w-5 h-5" style={{ color: session.current ? DESIGN.colors.primary : DESIGN.colors.textMuted }} />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <p className="font-semibold text-sm" style={{ color: DESIGN.colors.textPrimary }}>{session.device}</p>
                    {session.current && (
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: DESIGN.colors.primarySubtle, color: DESIGN.colors.primary }}>
                        Current
                      </span>
                    )}
                  </div>
                  <p className="text-xs" style={{ color: DESIGN.colors.textMuted }}>
                    {session.location} • {session.browser} • {session.time}
                  </p>
                </div>
              </div>
              {!session.current && (
                <button className="text-xs font-bold px-3 py-1.5 rounded-lg transition-all hover:bg-red-50" style={{ color: DESIGN.colors.error }}>
                  Revoke
                </button>
              )}
            </div>
          ))}
        </div>
      </SectionCard>
      
      <SectionCard title="Login History" description="Recent login activity">
        <div className="space-y-2">
          {[
            { time: 'Today, 9:32 AM', location: 'New York, US', status: 'success' },
            { time: 'Yesterday, 2:15 PM', location: 'New York, US', status: 'success' },
            { time: 'Feb 18, 8:45 AM', location: 'Boston, US', status: 'success' },
            { time: 'Feb 17, 11:20 PM', location: 'Unknown', status: 'blocked' }
          ].map((login, idx) => (
            <div key={idx} className="flex items-center justify-between py-3 border-b" style={{ borderColor: DESIGN.colors.borderSubtle }}>
              <div className="flex items-center gap-3">
                <div 
                  className="w-2 h-2 rounded-full"
                  style={{ backgroundColor: login.status === 'success' ? DESIGN.colors.success : DESIGN.colors.error }}
                />
                <span className="text-sm" style={{ color: DESIGN.colors.textPrimary }}>{login.time}</span>
              </div>
              <div className="flex items-center gap-4">
                <span className="text-sm" style={{ color: DESIGN.colors.textMuted }}>{login.location}</span>
                <span 
                  className="text-xs font-bold px-2 py-0.5 rounded-full"
                  style={{ 
                    backgroundColor: login.status === 'success' ? DESIGN.colors.successSubtle : DESIGN.colors.errorSubtle,
                    color: login.status === 'success' ? DESIGN.colors.success : DESIGN.colors.error
                  }}
                >
                  {login.status === 'success' ? 'Success' : 'Blocked'}
                </span>
              </div>
            </div>
          ))}
        </div>
      </SectionCard>
    </div>
  );
};

// Billing Section
const BillingSection = () => {
  const [billingCycle, setBillingCycle] = useState('annual');
  const currentPlan = 'scale';
  const currentSeats = 28;
  const planLimit = 40;
  
  return (
    <div className="space-y-6">
      {/* Current Plan Hero */}
      <SectionCard>
        <div 
          className="rounded-2xl p-6 relative overflow-hidden"
          style={{ 
            background: `linear-gradient(135deg, ${DESIGN.colors.primary}15 0%, ${DESIGN.colors.primary}05 100%)`,
            border: `2px solid ${DESIGN.colors.primary}`
          }}
        >
          <div className="absolute top-0 right-0 w-64 h-64 opacity-10" style={{ background: `radial-gradient(circle, ${DESIGN.colors.primary} 0%, transparent 70%)` }} />
          
          <div className="relative flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <span 
                  className="text-xs font-black px-3 py-1 rounded-lg"
                  style={{ backgroundColor: DESIGN.colors.primary, color: 'white' }}
                >
                  CURRENT PLAN
                </span>
                <span 
                  className="text-xs font-bold px-2 py-0.5 rounded-lg"
                  style={{ backgroundColor: `${DESIGN.colors.amber}20`, color: DESIGN.colors.amber }}
                >
                  Most Popular
                </span>
              </div>
              <p className="text-3xl font-black" style={{ color: DESIGN.colors.textPrimary }}>Scale Plan</p>
              <p className="text-sm mt-1" style={{ color: DESIGN.colors.textMuted }}>Up to 40 team members • $280/month (billed annually)</p>
            </div>
            <div className="text-right">
              <p className="text-sm font-bold" style={{ color: DESIGN.colors.primary }}>{currentSeats} of {planLimit} seats used</p>
              <div className="w-48 h-3 rounded-full mt-2 overflow-hidden" style={{ backgroundColor: '#E5E7EB' }}>
                <div 
                  className="h-full rounded-full"
                  style={{ 
                    width: `${(currentSeats / planLimit) * 100}%`, 
                    background: `linear-gradient(90deg, ${DESIGN.colors.primary} 0%, ${DESIGN.colors.primaryLight} 100%)`,
                    boxShadow: `0 0 10px ${DESIGN.colors.primary}50`
                  }}
                />
              </div>
              <p className="text-xs mt-2" style={{ color: DESIGN.colors.textMuted }}>{planLimit - currentSeats} seats remaining</p>
            </div>
          </div>
        </div>
      </SectionCard>
      
      {/* Billing Cycle Toggle */}
      <div className="flex items-center justify-center gap-4">
        <button 
          onClick={() => setBillingCycle('monthly')}
          className="px-5 py-2.5 rounded-xl text-sm font-bold transition-all"
          style={{ 
            backgroundColor: billingCycle === 'monthly' ? DESIGN.colors.primary : DESIGN.colors.background,
            color: billingCycle === 'monthly' ? 'white' : DESIGN.colors.textSecondary,
            boxShadow: billingCycle === 'monthly' ? DESIGN.shadows.glowPrimary : 'none'
          }}
        >
          Monthly
        </button>
        <button 
          onClick={() => setBillingCycle('annual')}
          className="px-5 py-2.5 rounded-xl text-sm font-bold transition-all flex items-center gap-2"
          style={{ 
            backgroundColor: billingCycle === 'annual' ? DESIGN.colors.primary : DESIGN.colors.background,
            color: billingCycle === 'annual' ? 'white' : DESIGN.colors.textSecondary,
            boxShadow: billingCycle === 'annual' ? DESIGN.shadows.glowPrimary : 'none'
          }}
        >
          Annual
          <span 
            className="text-[10px] font-black px-2 py-0.5 rounded-lg"
            style={{ 
              backgroundColor: billingCycle === 'annual' ? 'rgba(255,255,255,0.25)' : DESIGN.colors.successSubtle, 
              color: billingCycle === 'annual' ? 'white' : DESIGN.colors.success 
            }}
          >
            Save 20%
          </span>
        </button>
      </div>
      
      {/* Plans Grid */}
      <div className="grid grid-cols-4 gap-5">
        {BILLING_PLANS.map(plan => {
          const isCurrentPlan = plan.id === currentPlan;
          const price = billingCycle === 'annual' ? Math.round(plan.price * 0.8) : plan.price;
          
          return (
            <div 
              key={plan.id}
              className={`rounded-2xl p-6 transition-all relative overflow-hidden ${isCurrentPlan ? '' : 'hover:shadow-xl hover:-translate-y-1'}`}
              style={{ 
                backgroundColor: isCurrentPlan ? `${plan.color}10` : DESIGN.colors.card,
                border: `2px solid ${isCurrentPlan ? plan.color : DESIGN.colors.border}`
              }}
            >
              {plan.popular && (
                <div 
                  className="absolute top-3 right-3 text-[10px] font-black px-2 py-1 rounded-lg"
                  style={{ backgroundColor: plan.color, color: 'white' }}
                >
                  POPULAR
                </div>
              )}
              
              <p className="font-bold text-lg mb-1" style={{ color: plan.color }}>{plan.name}</p>
              <div className="flex items-baseline gap-1 mb-1">
                <span className="text-4xl font-black" style={{ color: DESIGN.colors.textPrimary }}>${price}</span>
                <span className="text-sm" style={{ color: DESIGN.colors.textMuted }}>/mo</span>
              </div>
              <p className="text-xs mb-5" style={{ color: DESIGN.colors.textMuted }}>Up to {plan.users} users</p>
              
              <ul className="space-y-2.5 mb-6">
                {plan.features.map((f, i) => (
                  <li key={i} className="flex items-center gap-2 text-xs" style={{ color: DESIGN.colors.textSecondary }}>
                    <Check className="w-4 h-4 flex-shrink-0" style={{ color: plan.color }} />
                    {f}
                  </li>
                ))}
              </ul>
              
              <Button 
                className="w-full rounded-xl text-sm font-bold"
                variant={isCurrentPlan ? 'outline' : 'default'}
                disabled={isCurrentPlan}
                style={!isCurrentPlan ? { 
                  background: `linear-gradient(135deg, ${plan.color} 0%, ${plan.color}DD 100%)`,
                  boxShadow: `0 0 15px ${plan.color}30`
                } : { borderColor: plan.color, color: plan.color }}
              >
                {isCurrentPlan ? 'Current Plan' : 'Upgrade'}
              </Button>
            </div>
          );
        })}
      </div>
      
      <p className="text-center text-sm" style={{ color: DESIGN.colors.textMuted }}>
        Need more users? Additional seats are <strong>$20/user/month</strong> beyond your plan limit.
      </p>
      
      {/* Payment Method */}
      <SectionCard title="Payment Method">
        <div className="flex items-center justify-between p-5 rounded-xl" style={{ backgroundColor: DESIGN.colors.background }}>
          <div className="flex items-center gap-5">
            <div className="w-16 h-10 rounded-lg bg-gradient-to-r from-blue-600 to-blue-400 flex items-center justify-center text-white text-sm font-black">
              VISA
            </div>
            <div>
              <p className="font-bold" style={{ color: DESIGN.colors.textPrimary }}>•••• •••• •••• 4242</p>
              <p className="text-xs" style={{ color: DESIGN.colors.textMuted }}>Expires 12/2026</p>
            </div>
          </div>
          <Button variant="outline" className="rounded-xl">Update Card</Button>
        </div>
      </SectionCard>
      
      {/* Invoice History */}
      <SectionCard 
        title="Invoice History"
        headerAction={
          <Button variant="ghost" size="sm" className="text-xs rounded-lg" style={{ color: DESIGN.colors.primary }}>
            Download All
          </Button>
        }
      >
        <div className="space-y-2">
          {[
            { date: 'Feb 1, 2026', amount: '$280.00', status: 'Paid', invoice: 'INV-2026-0201' },
            { date: 'Jan 1, 2026', amount: '$280.00', status: 'Paid', invoice: 'INV-2026-0101' },
            { date: 'Dec 1, 2025', amount: '$280.00', status: 'Paid', invoice: 'INV-2025-1201' },
            { date: 'Nov 1, 2025', amount: '$280.00', status: 'Paid', invoice: 'INV-2025-1101' }
          ].map((inv, idx) => (
            <div key={idx} className="flex items-center justify-between py-4 border-b" style={{ borderColor: DESIGN.colors.borderSubtle }}>
              <div className="flex items-center gap-4">
                <FileText className="w-5 h-5" style={{ color: DESIGN.colors.textMuted }} />
                <div>
                  <span className="text-sm font-semibold" style={{ color: DESIGN.colors.textPrimary }}>{inv.date}</span>
                  <p className="text-xs" style={{ color: DESIGN.colors.textMuted }}>{inv.invoice}</p>
                </div>
              </div>
              <div className="flex items-center gap-5">
                <span className="text-sm font-bold" style={{ color: DESIGN.colors.textPrimary }}>{inv.amount}</span>
                <span className="text-xs font-bold px-2.5 py-1 rounded-lg" style={{ backgroundColor: DESIGN.colors.successSubtle, color: DESIGN.colors.success }}>{inv.status}</span>
                <button className="p-2 rounded-lg hover:bg-gray-50 transition-colors">
                  <Download className="w-4 h-4" style={{ color: DESIGN.colors.textMuted }} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </SectionCard>
    </div>
  );
};

// Integrations Section (The Big One)
const IntegrationsSection = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [selectedIntegration, setSelectedIntegration] = useState(null);
  
  // Count connected integrations
  const totalIntegrations = Object.values(INTEGRATIONS).flat().length;
  const connectedCount = Object.values(INTEGRATIONS).flat().filter(i => i.connected).length;
  
  // Filter integrations
  const getFilteredIntegrations = () => {
    let filtered = [];
    
    if (activeCategory === 'all') {
      filtered = Object.values(INTEGRATIONS).flat();
    } else {
      filtered = INTEGRATIONS[activeCategory] || [];
    }
    
    if (searchQuery) {
      filtered = filtered.filter(i => 
        i.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        i.description.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    return filtered;
  };

  return (
    <div className="space-y-6">
      {/* Stats Bar */}
      <div 
        className="rounded-2xl p-6 flex items-center justify-between"
        style={{ 
          background: `linear-gradient(135deg, ${DESIGN.colors.primary}10 0%, #8B5CF610 100%)`,
          border: `1px solid ${DESIGN.colors.primary}30`
        }}
      >
        <div className="flex items-center gap-10">
          <div>
            <p className="text-4xl font-black" style={{ color: DESIGN.colors.textPrimary }}>{totalIntegrations}</p>
            <p className="text-xs font-bold uppercase tracking-wider" style={{ color: DESIGN.colors.textMuted }}>Available</p>
          </div>
          <div className="w-px h-12" style={{ backgroundColor: DESIGN.colors.border }} />
          <div>
            <p className="text-4xl font-black" style={{ color: DESIGN.colors.primary }}>{connectedCount}</p>
            <p className="text-xs font-bold uppercase tracking-wider" style={{ color: DESIGN.colors.textMuted }}>Connected</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Sparkles className="w-6 h-6" style={{ color: DESIGN.colors.primary }} />
          <p className="text-sm font-semibold" style={{ color: DESIGN.colors.textSecondary }}>
            Supercharge your workflow by connecting more tools
          </p>
        </div>
      </div>
      
      {/* Search Bar */}
      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5" style={{ color: DESIGN.colors.textMuted }} />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search integrations..."
          className="w-full pl-12 pr-4 py-4 rounded-xl text-sm outline-none transition-all focus:ring-2"
          style={{ 
            backgroundColor: DESIGN.colors.card,
            border: `1px solid ${DESIGN.colors.border}`,
            color: DESIGN.colors.textPrimary,
            '--tw-ring-color': DESIGN.colors.primary
          }}
        />
      </div>
      
      {/* Category Tabs */}
      <div className="flex gap-2 flex-wrap">
        {INTEGRATION_CATEGORIES.map(cat => {
          const Icon = cat.icon;
          const isActive = activeCategory === cat.id;
          return (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold transition-all"
              style={{
                backgroundColor: isActive ? DESIGN.colors.primary : DESIGN.colors.card,
                color: isActive ? 'white' : DESIGN.colors.textSecondary,
                boxShadow: isActive ? DESIGN.shadows.glowPrimary : DESIGN.shadows.sm,
                border: `1px solid ${isActive ? DESIGN.colors.primary : DESIGN.colors.border}`
              }}
            >
              <Icon className="w-4 h-4" />
              {cat.label}
            </button>
          );
        })}
      </div>
      
      {/* Migration Section */}
      {(activeCategory === 'all') && !searchQuery && (
        <div 
          className="rounded-2xl p-6"
          style={{ 
            background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(139, 92, 246, 0.05) 100%)',
            border: '1px solid rgba(139, 92, 246, 0.3)'
          }}
        >
          <div className="flex items-center gap-3 mb-2">
            <Import className="w-6 h-6" style={{ color: '#8B5CF6' }} />
            <h3 className="text-lg font-bold" style={{ color: DESIGN.colors.textPrimary }}>Migration & Import</h3>
            <span className="text-[10px] font-black px-2.5 py-1 rounded-lg" style={{ backgroundColor: '#8B5CF620', color: '#8B5CF6' }}>
              One-click import
            </span>
          </div>
          <p className="text-sm mb-5" style={{ color: DESIGN.colors.textMuted }}>
            Moving from another platform? Import your existing data in one click.
          </p>
          <div className="grid grid-cols-4 gap-4">
            {INTEGRATIONS.migration.map(integration => (
              <IntegrationCard 
                key={integration.id} 
                integration={integration} 
                isImport={true}
                onConnect={setSelectedIntegration}
              />
            ))}
          </div>
        </div>
      )}
      
      {/* Integrations Grid */}
      <div className="grid grid-cols-3 gap-5">
        {getFilteredIntegrations()
          .filter(i => activeCategory !== 'all' || !INTEGRATIONS.migration.includes(i))
          .map(integration => (
            <IntegrationCard 
              key={integration.id} 
              integration={integration}
              isImport={false}
              onConnect={setSelectedIntegration}
            />
          ))}
      </div>
      
      {/* Integration Detail Modal */}
      {selectedIntegration && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setSelectedIntegration(null)} />
          <div 
            className="relative w-full max-w-lg rounded-2xl overflow-hidden"
            style={{ backgroundColor: DESIGN.colors.card, boxShadow: DESIGN.shadows.xl }}
          >
            {/* Modal Header with Lane Color */}
            <div 
              className="p-6"
              style={{ 
                background: selectedIntegration.lane 
                  ? `linear-gradient(135deg, ${COGNITIVE_LANES.find(l => l.name === selectedIntegration.lane)?.color || DESIGN.colors.primary}15 0%, transparent 100%)` 
                  : undefined
              }}
            >
              <button 
                onClick={() => setSelectedIntegration(null)}
                className="absolute top-4 right-4 p-2 rounded-lg hover:bg-black/5 transition-colors"
              >
                <X className="w-5 h-5" style={{ color: DESIGN.colors.textMuted }} />
              </button>
              
              <div className="flex items-center gap-5">
                <div 
                  className="w-20 h-20 rounded-2xl flex items-center justify-center text-4xl"
                  style={{ backgroundColor: DESIGN.colors.background }}
                >
                  {selectedIntegration.logo}
                </div>
                <div>
                  <h3 className="text-2xl font-black" style={{ color: DESIGN.colors.textPrimary }}>{selectedIntegration.name}</h3>
                  <p className="text-sm mt-1" style={{ color: DESIGN.colors.textMuted }}>{selectedIntegration.description}</p>
                </div>
              </div>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="p-4 rounded-xl" style={{ backgroundColor: DESIGN.colors.background }}>
                <p className="text-xs font-black uppercase tracking-wider mb-2" style={{ color: DESIGN.colors.textMuted }}>What syncs</p>
                <p className="text-sm" style={{ color: DESIGN.colors.textPrimary }}>
                  Tasks, projects, deadlines, team assignments, and activity logs
                </p>
              </div>
              
              {selectedIntegration.lane && (
                <div className="p-4 rounded-xl" style={{ backgroundColor: DESIGN.colors.background }}>
                  <p className="text-xs font-black uppercase tracking-wider mb-2" style={{ color: DESIGN.colors.textMuted }}>Cognitive Lane</p>
                  <div className="flex items-center gap-2">
                    <Layers className="w-4 h-4" style={{ color: DESIGN.colors.primary }} />
                    <p className="text-sm font-bold" style={{ color: DESIGN.colors.primary }}>{selectedIntegration.lane}</p>
                  </div>
                </div>
              )}
              
              {selectedIntegration.connected && (
                <div className="p-4 rounded-xl" style={{ backgroundColor: DESIGN.colors.successSubtle }}>
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-3 h-3 rounded-full animate-pulse" style={{ backgroundColor: DESIGN.colors.success }} />
                    <p className="text-sm font-bold" style={{ color: DESIGN.colors.success }}>Connected</p>
                  </div>
                  <p className="text-xs" style={{ color: DESIGN.colors.textMuted }}>Last synced: {selectedIntegration.lastSync}</p>
                </div>
              )}
              
              <div className="p-4 rounded-xl" style={{ backgroundColor: DESIGN.colors.background }}>
                <p className="text-xs font-black uppercase tracking-wider mb-2" style={{ color: DESIGN.colors.textMuted }}>Required permissions</p>
                <ul className="space-y-1.5 text-sm" style={{ color: DESIGN.colors.textSecondary }}>
                  <li className="flex items-center gap-2"><Check className="w-3.5 h-3.5" style={{ color: DESIGN.colors.success }} /> Read account data</li>
                  <li className="flex items-center gap-2"><Check className="w-3.5 h-3.5" style={{ color: DESIGN.colors.success }} /> Access projects and tasks</li>
                  <li className="flex items-center gap-2"><Check className="w-3.5 h-3.5" style={{ color: DESIGN.colors.success }} /> Sync team members</li>
                </ul>
              </div>
            </div>
            
            <div className="p-6 border-t flex gap-3" style={{ borderColor: DESIGN.colors.border }}>
              {selectedIntegration.connected ? (
                <>
                  <Button variant="outline" className="flex-1 rounded-xl">Manage Permissions</Button>
                  <Button 
                    variant="outline" 
                    className="rounded-xl"
                    style={{ borderColor: DESIGN.colors.error, color: DESIGN.colors.error }}
                  >
                    Disconnect
                  </Button>
                </>
              ) : (
                <Button 
                  className="w-full rounded-xl font-bold"
                  style={{ 
                    background: `linear-gradient(135deg, ${DESIGN.colors.primary} 0%, ${DESIGN.colors.primaryDark} 100%)`,
                    boxShadow: DESIGN.shadows.glowPrimary
                  }}
                >
                  <Zap className="w-4 h-4 mr-2" />
                  Connect {selectedIntegration.name}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Appearance Section
const AppearanceSection = () => {
  const [theme, setTheme] = useState('light');
  const [accentColor, setAccentColor] = useState('#00C9A7');
  
  const accentColors = [
    { id: 'teal', color: '#00C9A7', name: 'Teal' },
    { id: 'blue', color: '#3B82F6', name: 'Blue' },
    { id: 'purple', color: '#8B5CF6', name: 'Purple' },
    { id: 'pink', color: '#EC4899', name: 'Pink' },
    { id: 'orange', color: '#F59E0B', name: 'Orange' },
    { id: 'red', color: '#EF4444', name: 'Red' },
    { id: 'green', color: '#10B981', name: 'Green' },
    { id: 'indigo', color: '#6366F1', name: 'Indigo' }
  ];
  
  return (
    <div className="space-y-6">
      <SectionCard title="Theme" description="Choose your preferred visual mode">
        <div className="grid grid-cols-3 gap-5">
          {[
            { id: 'light', label: 'Light', icon: Sun, desc: 'Clean and bright' },
            { id: 'dark', label: 'Dark', icon: Moon, desc: 'Easy on the eyes' },
            { id: 'system', label: 'System', icon: Monitor, desc: 'Match your device' }
          ].map(t => {
            const Icon = t.icon;
            const isActive = theme === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setTheme(t.id)}
                className="p-6 rounded-2xl transition-all text-left"
                style={{
                  backgroundColor: isActive ? `${DESIGN.colors.primary}15` : DESIGN.colors.background,
                  border: `2px solid ${isActive ? DESIGN.colors.primary : DESIGN.colors.border}`,
                  boxShadow: isActive ? DESIGN.shadows.glowPrimary : 'none'
                }}
              >
                <Icon 
                  className="w-8 h-8 mb-3" 
                  style={{ color: isActive ? DESIGN.colors.primary : DESIGN.colors.textMuted }} 
                />
                <p className="font-bold" style={{ color: DESIGN.colors.textPrimary }}>{t.label}</p>
                <p className="text-xs mt-1" style={{ color: DESIGN.colors.textMuted }}>{t.desc}</p>
              </button>
            );
          })}
        </div>
      </SectionCard>
      
      <SectionCard title="Accent Color" description="Customize your workspace accent color">
        <div className="flex gap-4 flex-wrap">
          {accentColors.map(c => (
            <button
              key={c.id}
              onClick={() => setAccentColor(c.color)}
              className="group relative"
            >
              <div
                className="w-14 h-14 rounded-2xl transition-all hover:scale-110"
                style={{ 
                  backgroundColor: c.color,
                  boxShadow: accentColor === c.color ? `0 0 25px ${c.color}60, inset 0 0 20px rgba(255,255,255,0.2)` : 'none',
                  border: accentColor === c.color ? '3px solid white' : 'none'
                }}
              />
              {accentColor === c.color && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <Check className="w-6 h-6 text-white" />
                </div>
              )}
              <p className="text-xs font-medium mt-2 text-center" style={{ color: DESIGN.colors.textMuted }}>{c.name}</p>
            </button>
          ))}
        </div>
      </SectionCard>
      
      <div className="flex justify-end">
        <SaveButton />
      </div>
    </div>
  );
};

// Notifications Section
const NotificationsSection = ({ type }) => {
  const [settings, setSettings] = useState({
    taskAssigned: true,
    taskCompleted: true,
    phaseOverdue: true,
    burnoutAlert: true,
    teamUpdates: false,
    weeklyDigest: true,
    executiveReview: true,
    collabActivity: true,
    projectMilestone: true,
    mentionNotify: true
  });
  
  const toggleSetting = (key) => {
    setSettings(prev => ({ ...prev, [key]: !prev[key] }));
  };
  
  const notificationGroups = {
    tasks: [
      { key: 'taskAssigned', label: 'Task Assigned', desc: 'When a new task is assigned to you' },
      { key: 'taskCompleted', label: 'Task Completed', desc: 'When tasks you\'re tracking are completed' },
      { key: 'phaseOverdue', label: 'Phase Overdue Alerts', desc: 'When project phases become overdue' }
    ],
    team: [
      { key: 'burnoutAlert', label: 'Burnout Risk Alerts', desc: 'When team members show burnout risk' },
      { key: 'teamUpdates', label: 'Team Updates', desc: 'General team activity and announcements' },
      { key: 'mentionNotify', label: 'Mentions', desc: 'When someone mentions you' }
    ],
    projects: [
      { key: 'collabActivity', label: 'Collaboration Activity', desc: 'Updates on collaborations you\'re part of' },
      { key: 'projectMilestone', label: 'Project Milestones', desc: 'When project milestones are reached' },
      { key: 'executiveReview', label: 'Executive Reviews', desc: 'When items need your review or approval' }
    ],
    digest: [
      { key: 'weeklyDigest', label: 'Weekly Digest', desc: 'Weekly summary of your workspace activity' }
    ]
  };
  
  const getTitle = () => {
    switch (type) {
      case 'inapp': return 'In-App Notifications';
      case 'email': return 'Email Notifications';
      case 'push': return 'Push Notifications';
      default: return 'Notifications';
    }
  };
  
  return (
    <div className="space-y-6">
      {Object.entries(notificationGroups).map(([group, items]) => (
        <SectionCard 
          key={group} 
          title={group === 'tasks' ? 'Task Notifications' : group === 'team' ? 'Team Notifications' : group === 'projects' ? 'Project Notifications' : 'Digest'}
        >
          <div>
            {items.map((item, idx) => (
              <PremiumToggle
                key={item.key}
                label={item.label}
                description={item.desc}
                checked={settings[item.key]}
                onChange={() => toggleSetting(item.key)}
              />
            ))}
          </div>
        </SectionCard>
      ))}
      
      <div className="flex justify-end">
        <SaveButton />
      </div>
    </div>
  );
};

// Team Management Section
const TeamManagementSection = () => {
  const [searchQuery, setSearchQuery] = useState('');
  
  const filteredMembers = TEAM_MEMBERS.filter(m => 
    m.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    m.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
    m.role.toLowerCase().includes(searchQuery.toLowerCase())
  );
  
  const getLaneColor = (laneId) => {
    const lane = COGNITIVE_LANES.find(l => l.id === laneId);
    return lane?.color || DESIGN.colors.textMuted;
  };
  
  const getLevelColor = (level) => {
    return DESIGN.levels[level]?.color || DESIGN.colors.textMuted;
  };
  
  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-4 gap-5">
        {[
          { label: 'Total Members', value: TEAM_MEMBERS.length, color: DESIGN.colors.primary },
          { label: 'Active', value: TEAM_MEMBERS.filter(m => m.status === 'active').length, color: DESIGN.colors.success },
          { label: 'Away', value: TEAM_MEMBERS.filter(m => m.status === 'away').length, color: DESIGN.colors.amber },
          { label: 'Seats Remaining', value: 12, color: DESIGN.colors.blue }
        ].map((stat, idx) => (
          <div 
            key={idx} 
            className="p-5 rounded-2xl"
            style={{ backgroundColor: DESIGN.colors.card, boxShadow: DESIGN.shadows.md }}
          >
            <p className="text-xs font-bold uppercase tracking-wider" style={{ color: DESIGN.colors.textMuted }}>{stat.label}</p>
            <p className="text-3xl font-black mt-1" style={{ color: stat.color }}>{stat.value}</p>
          </div>
        ))}
      </div>
      
      <SectionCard 
        title="Team Members"
        headerAction={
          <Button 
            className="rounded-xl"
            style={{ 
              background: `linear-gradient(135deg, ${DESIGN.colors.primary} 0%, ${DESIGN.colors.primaryDark} 100%)`,
              boxShadow: DESIGN.shadows.glowPrimary
            }}
          >
            <Plus className="w-4 h-4 mr-2" />
            Invite Member
          </Button>
        }
      >
        {/* Search */}
        <div className="relative mb-5">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: DESIGN.colors.textMuted }} />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search team members..."
            className="w-full pl-11 pr-4 py-3 rounded-xl text-sm outline-none"
            style={{ 
              backgroundColor: DESIGN.colors.background,
              border: `1px solid ${DESIGN.colors.border}`,
              color: DESIGN.colors.textPrimary
            }}
          />
        </div>
        
        {/* Members List */}
        <div className="space-y-3">
          {filteredMembers.map(member => (
            <div 
              key={member.id}
              className="flex items-center justify-between p-4 rounded-xl transition-all hover:shadow-md"
              style={{ backgroundColor: DESIGN.colors.background }}
            >
              <div className="flex items-center gap-4">
                <div 
                  className="w-12 h-12 rounded-xl flex items-center justify-center font-bold"
                  style={{ 
                    background: `linear-gradient(135deg, ${getLaneColor(member.lane)} 0%, ${getLaneColor(member.lane)}CC 100%)`,
                    color: 'white'
                  }}
                >
                  {member.avatar}
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <p className="font-bold" style={{ color: DESIGN.colors.textPrimary }}>{member.name}</p>
                    <span 
                      className="text-[10px] font-bold px-2 py-0.5 rounded-lg"
                      style={{ backgroundColor: `${getLevelColor(member.level)}20`, color: getLevelColor(member.level) }}
                    >
                      {member.level}
                    </span>
                  </div>
                  <p className="text-sm" style={{ color: DESIGN.colors.textMuted }}>{member.role}</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div 
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg"
                  style={{ backgroundColor: `${getLaneColor(member.lane)}15` }}
                >
                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: getLaneColor(member.lane) }} />
                  <span className="text-xs font-medium" style={{ color: getLaneColor(member.lane) }}>
                    {COGNITIVE_LANES.find(l => l.id === member.lane)?.name}
                  </span>
                </div>
                <div 
                  className="w-2 h-2 rounded-full"
                  style={{ backgroundColor: member.status === 'active' ? DESIGN.colors.success : DESIGN.colors.amber }}
                />
                <Button variant="ghost" size="sm" className="rounded-lg">
                  <MoreHorizontal className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </SectionCard>
    </div>
  );
};

// Workspace General Section
const WorkspaceGeneralSection = () => (
  <div className="space-y-6">
    <SectionCard title="Workspace Details" description="Configure your workspace identity">
      <div className="flex items-start gap-8 mb-8">
        <div className="relative group">
          <div 
            className="w-24 h-24 rounded-2xl flex items-center justify-center text-3xl cursor-pointer transition-all group-hover:scale-105"
            style={{ 
              backgroundColor: DESIGN.colors.background,
              border: `2px dashed ${DESIGN.colors.border}`
            }}
          >
            <Building2 className="w-10 h-10" style={{ color: DESIGN.colors.textMuted }} />
          </div>
          <button 
            className="absolute -bottom-2 -right-2 w-8 h-8 rounded-xl flex items-center justify-center"
            style={{ backgroundColor: DESIGN.colors.card, boxShadow: DESIGN.shadows.lg }}
          >
            <Upload className="w-4 h-4" style={{ color: DESIGN.colors.primary }} />
          </button>
        </div>
        <div className="flex-1">
          <h4 className="text-lg font-bold" style={{ color: DESIGN.colors.textPrimary }}>Workspace Logo</h4>
          <p className="text-sm" style={{ color: DESIGN.colors.textMuted }}>Upload your company logo. Recommended size: 256x256px</p>
          <Button variant="ghost" size="sm" className="mt-3 text-xs rounded-lg" style={{ color: DESIGN.colors.primary }}>
            Upload Logo
          </Button>
        </div>
      </div>
      
      <div className="grid grid-cols-2 gap-5">
        <FormInput label="Workspace Name" value="Acme Corp" placeholder="Enter workspace name" />
        <FormInput label="Workspace URL" value="acme-corp" placeholder="workspace-url" helper="cognitiveos.com/acme-corp" />
      </div>
    </SectionCard>
    
    <SectionCard title="Regional Settings">
      <div className="grid grid-cols-2 gap-5">
        <div className="space-y-2">
          <label className="text-sm font-semibold" style={{ color: DESIGN.colors.textPrimary }}>Default Timezone</label>
          <select 
            className="w-full px-4 py-3 rounded-xl text-sm"
            style={{ backgroundColor: DESIGN.colors.background, border: `1px solid ${DESIGN.colors.border}`, color: DESIGN.colors.textPrimary }}
          >
            <option>America/New_York (EST)</option>
            <option>America/Los_Angeles (PST)</option>
            <option>Europe/London (GMT)</option>
          </select>
        </div>
        <div className="space-y-2">
          <label className="text-sm font-semibold" style={{ color: DESIGN.colors.textPrimary }}>Date Format</label>
          <select 
            className="w-full px-4 py-3 rounded-xl text-sm"
            style={{ backgroundColor: DESIGN.colors.background, border: `1px solid ${DESIGN.colors.border}`, color: DESIGN.colors.textPrimary }}
          >
            <option>MM/DD/YYYY</option>
            <option>DD/MM/YYYY</option>
            <option>YYYY-MM-DD</option>
          </select>
        </div>
      </div>
    </SectionCard>
    
    <div className="flex justify-end">
      <SaveButton />
    </div>
  </div>
);

// API Access Section
const APIAccessSection = () => {
  const [showKey, setShowKey] = useState(false);
  const apiKey = 'cos_live_sk_xxxxxxxxxxxxxxxxxxxxxxxxxxxxx';
  
  return (
    <div className="space-y-6">
      <SectionCard title="API Keys" description="Manage your API keys for custom integrations">
        <div 
          className="p-5 rounded-xl mb-5"
          style={{ backgroundColor: DESIGN.colors.background, border: `1px solid ${DESIGN.colors.border}` }}
        >
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="font-bold" style={{ color: DESIGN.colors.textPrimary }}>Production API Key</p>
              <p className="text-xs" style={{ color: DESIGN.colors.textMuted }}>Created Feb 1, 2026 • Last used 2 hours ago</p>
            </div>
            <span className="text-xs font-bold px-3 py-1.5 rounded-lg" style={{ backgroundColor: DESIGN.colors.successSubtle, color: DESIGN.colors.success }}>Active</span>
          </div>
          <div className="flex items-center gap-2">
            <code 
              className="flex-1 px-4 py-3 rounded-xl text-sm font-mono"
              style={{ backgroundColor: DESIGN.colors.card, color: DESIGN.colors.textPrimary }}
            >
              {showKey ? apiKey : '•••••••••••••••••••••••••••••••••••'}
            </code>
            <button onClick={() => setShowKey(!showKey)} className="p-3 rounded-xl hover:bg-gray-100 transition-colors">
              {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
            <button className="p-3 rounded-xl hover:bg-gray-100 transition-colors">
              <Copy className="w-4 h-4" />
            </button>
          </div>
        </div>
        
        <div className="flex gap-3">
          <Button 
            variant="outline" 
            className="rounded-xl"
            style={{ borderColor: DESIGN.colors.primary, color: DESIGN.colors.primary }}
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Regenerate Key
          </Button>
          <Button 
            variant="outline" 
            className="rounded-xl"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create New Key
          </Button>
        </div>
      </SectionCard>
      
      <SectionCard title="API Usage" description="Monitor your API usage this month">
        <div className="grid grid-cols-4 gap-5 mb-6">
          {[
            { label: 'Total Requests', value: '12,847', color: DESIGN.colors.textPrimary },
            { label: 'Success Rate', value: '99.9%', color: DESIGN.colors.success },
            { label: 'Avg Response', value: '45ms', color: DESIGN.colors.blue },
            { label: 'Rate Limit', value: '85%', color: DESIGN.colors.amber }
          ].map((stat, idx) => (
            <div key={idx} className="p-5 rounded-xl text-center" style={{ backgroundColor: DESIGN.colors.background }}>
              <p className="text-3xl font-black" style={{ color: stat.color }}>{stat.value}</p>
              <p className="text-xs font-bold uppercase tracking-wider mt-1" style={{ color: DESIGN.colors.textMuted }}>{stat.label}</p>
            </div>
          ))}
        </div>
        <a href="#" className="text-sm font-bold flex items-center gap-2" style={{ color: DESIGN.colors.primary }}>
          View API Documentation
          <ArrowUpRight className="w-4 h-4" />
        </a>
      </SectionCard>
    </div>
  );
};

// Audit Log Section
const AuditLogSection = () => {
  const [filterType, setFilterType] = useState('all');
  
  const typeColors = {
    billing: DESIGN.colors.amber,
    integration: DESIGN.colors.purple,
    project: DESIGN.colors.primary,
    team: DESIGN.colors.blue,
    settings: DESIGN.colors.textSecondary,
    data: DESIGN.colors.success,
    security: DESIGN.colors.error
  };
  
  const filteredLogs = filterType === 'all' 
    ? AUDIT_LOGS 
    : AUDIT_LOGS.filter(l => l.type === filterType);
  
  return (
    <div className="space-y-6">
      <SectionCard 
        title="Activity Log" 
        description="View all actions taken across the platform"
        headerAction={
          <div className="flex gap-2">
            {['all', 'team', 'billing', 'security'].map(type => (
              <button
                key={type}
                onClick={() => setFilterType(type)}
                className="px-3 py-1.5 rounded-lg text-xs font-bold transition-all"
                style={{
                  backgroundColor: filterType === type ? DESIGN.colors.primary : DESIGN.colors.background,
                  color: filterType === type ? 'white' : DESIGN.colors.textMuted
                }}
              >
                {type.charAt(0).toUpperCase() + type.slice(1)}
              </button>
            ))}
          </div>
        }
      >
        <div className="space-y-3">
          {filteredLogs.map(log => (
            <div 
              key={log.id}
              className="flex items-center justify-between p-4 rounded-xl transition-all hover:shadow-md"
              style={{ backgroundColor: DESIGN.colors.background }}
            >
              <div className="flex items-center gap-4">
                <div 
                  className="w-10 h-10 rounded-xl flex items-center justify-center text-sm font-bold"
                  style={{ 
                    backgroundColor: `${typeColors[log.type]}15`,
                    color: typeColors[log.type]
                  }}
                >
                  {log.user.split(' ').map(n => n[0]).join('')}
                </div>
                <div>
                  <p className="text-sm" style={{ color: DESIGN.colors.textPrimary }}>
                    <span className="font-bold">{log.user}</span> {log.action}
                  </p>
                  <p className="text-xs" style={{ color: DESIGN.colors.textMuted }}>{log.timestamp} • IP: {log.ip}</p>
                </div>
              </div>
              <span 
                className="text-[10px] font-bold px-2.5 py-1 rounded-lg uppercase"
                style={{ backgroundColor: `${typeColors[log.type]}15`, color: typeColors[log.type] }}
              >
                {log.type}
              </span>
            </div>
          ))}
        </div>
      </SectionCard>
    </div>
  );
};

// Cognitive Lane Configuration Section
const LaneConfigSection = () => (
  <div className="space-y-6">
    <SectionCard title="Cognitive Lanes" description="Customize the ten cognitive lanes for your organization">
      <div className="space-y-4">
        {COGNITIVE_LANES.map(lane => (
          <div 
            key={lane.id}
            className="flex items-center justify-between p-4 rounded-xl transition-all hover:shadow-md"
            style={{ 
              backgroundColor: DESIGN.colors.background,
              borderLeft: `4px solid ${lane.color}`
            }}
          >
            <div className="flex items-center gap-4">
              <div 
                className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl"
                style={{ backgroundColor: `${lane.color}15` }}
              >
                {lane.icon}
              </div>
              <div>
                <p className="font-bold" style={{ color: DESIGN.colors.textPrimary }}>{lane.name}</p>
                <p className="text-sm" style={{ color: DESIGN.colors.textMuted }}>{lane.description}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div 
                className="w-8 h-8 rounded-lg"
                style={{ backgroundColor: lane.color }}
              />
              <Button variant="ghost" size="sm" className="rounded-lg">
                <Edit3 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        ))}
      </div>
    </SectionCard>
    
    <div className="flex justify-end">
      <SaveButton />
    </div>
  </div>
);

// Level Definitions Section
const LevelDefinitionsSection = () => (
  <div className="space-y-6">
    <SectionCard title="Cognitive Levels" description="Customize how the four levels are defined and labeled">
      <div className="grid grid-cols-2 gap-5">
        {Object.entries(DESIGN.levels).map(([key, level]) => (
          <div 
            key={key}
            className="p-5 rounded-xl"
            style={{ 
              backgroundColor: DESIGN.colors.background,
              border: `2px solid ${level.color}30`
            }}
          >
            <div className="flex items-center gap-3 mb-4">
              <div 
                className="w-12 h-12 rounded-xl flex items-center justify-center font-black text-lg"
                style={{ backgroundColor: `${level.color}15`, color: level.color }}
              >
                {key}
              </div>
              <div>
                <p className="font-bold" style={{ color: DESIGN.colors.textPrimary }}>{level.name}</p>
                <p className="text-xs font-bold" style={{ color: level.color }}>{level.label}</p>
              </div>
            </div>
            <FormInput 
              label="Display Name" 
              value={level.name} 
              placeholder="Enter level name"
            />
          </div>
        ))}
      </div>
    </SectionCard>
    
    <div className="flex justify-end">
      <SaveButton />
    </div>
  </div>
);

// Load Thresholds Section
const LoadThresholdsSection = () => {
  const [thresholds, setThresholds] = useState({
    warning: 70,
    alert: 85,
    critical: 95
  });
  
  return (
    <div className="space-y-6">
      <SectionCard title="Cognitive Load Thresholds" description="Configure at what load percentages the platform triggers warnings and alerts">
        <div className="space-y-8">
          {[
            { key: 'warning', label: 'Warning Threshold', color: DESIGN.colors.amber, desc: 'When to show warning indicators' },
            { key: 'alert', label: 'Alert Threshold', color: '#F97316', desc: 'When to send alert notifications' },
            { key: 'critical', label: 'Critical Threshold', color: DESIGN.colors.error, desc: 'When to trigger urgent interventions' }
          ].map(item => (
            <div key={item.key}>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="font-bold" style={{ color: DESIGN.colors.textPrimary }}>{item.label}</p>
                  <p className="text-sm" style={{ color: DESIGN.colors.textMuted }}>{item.desc}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-2xl font-black" style={{ color: item.color }}>{thresholds[item.key]}%</span>
                </div>
              </div>
              <div className="relative">
                <input
                  type="range"
                  min="50"
                  max="100"
                  value={thresholds[item.key]}
                  onChange={(e) => setThresholds(prev => ({ ...prev, [item.key]: parseInt(e.target.value) }))}
                  className="w-full h-3 rounded-full appearance-none cursor-pointer"
                  style={{ 
                    background: `linear-gradient(to right, ${item.color} 0%, ${item.color} ${(thresholds[item.key] - 50) * 2}%, #E5E7EB ${(thresholds[item.key] - 50) * 2}%, #E5E7EB 100%)`
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      </SectionCard>
      
      <div className="flex justify-end">
        <SaveButton />
      </div>
    </div>
  );
};

// Data & Privacy Section
const DataPrivacySection = () => (
  <div className="space-y-6">
    <SectionCard title="Data Export" description="Export all your organization's data">
      <div className="flex items-center justify-between p-5 rounded-xl" style={{ backgroundColor: DESIGN.colors.background }}>
        <div className="flex items-center gap-4">
          <div 
            className="w-12 h-12 rounded-xl flex items-center justify-center"
            style={{ backgroundColor: `${DESIGN.colors.primary}15` }}
          >
            <Download className="w-6 h-6" style={{ color: DESIGN.colors.primary }} />
          </div>
          <div>
            <p className="font-bold" style={{ color: DESIGN.colors.textPrimary }}>Export All Data</p>
            <p className="text-sm" style={{ color: DESIGN.colors.textMuted }}>Download a complete export of your workspace data</p>
          </div>
        </div>
        <Button variant="outline" className="rounded-xl">
          Request Export
        </Button>
      </div>
    </SectionCard>
    
    <SectionCard title="Data Retention" description="Configure how long data is retained">
      <div className="space-y-4">
        <PremiumToggle
          label="Auto-archive completed projects"
          description="Automatically archive projects 90 days after completion"
          checked={true}
          onChange={() => {}}
        />
        <PremiumToggle
          label="Delete audit logs after 1 year"
          description="Automatically remove audit logs older than 12 months"
          checked={false}
          onChange={() => {}}
        />
      </div>
    </SectionCard>
    
    <SectionCard title="Privacy Controls">
      <div className="space-y-4">
        <PremiumToggle
          label="Allow analytics collection"
          description="Help improve CognitiveOS with anonymous usage data"
          checked={true}
          onChange={() => {}}
        />
        <PremiumToggle
          label="Show profile to team members"
          description="Allow team members to see your profile information"
          checked={true}
          onChange={() => {}}
        />
      </div>
    </SectionCard>
  </div>
);

// Placeholder sections for remaining items
const PlaceholderSection = ({ title, description, icon: Icon = SettingsIcon }) => (
  <SectionCard title={title} description={description}>
    <div className="text-center py-16">
      <div 
        className="w-20 h-20 rounded-2xl mx-auto mb-5 flex items-center justify-center"
        style={{ backgroundColor: DESIGN.colors.background }}
      >
        <Icon className="w-10 h-10" style={{ color: DESIGN.colors.textMuted }} />
      </div>
      <p className="text-base font-semibold" style={{ color: DESIGN.colors.textSecondary }}>
        This section is ready for configuration
      </p>
      <p className="text-sm mt-2" style={{ color: DESIGN.colors.textMuted }}>
        Settings will appear here once enabled
      </p>
    </div>
  </SectionCard>
);

// ================================================
// MAIN SETTINGS PAGE
// ================================================
const Settings = () => {
  const [activeSection, setActiveSection] = useState('profile');
  
  const renderContent = () => {
    switch (activeSection) {
      case 'profile': return <ProfileSection />;
      case 'security': return <SecuritySection />;
      case 'billing': return <BillingSection />;
      case 'integrations': return <IntegrationsSection />;
      case 'appearance': return <AppearanceSection />;
      case 'inapp':
      case 'email':
      case 'push':
        return <NotificationsSection type={activeSection} />;
      case 'api': return <APIAccessSection />;
      case 'audit': return <AuditLogSection />;
      case 'general': return <WorkspaceGeneralSection />;
      case 'team': return <TeamManagementSection />;
      case 'privacy': return <DataPrivacySection />;
      case 'laneconfig': return <LaneConfigSection />;
      case 'levels': return <LevelDefinitionsSection />;
      case 'thresholds': return <LoadThresholdsSection />;
      case 'roles': return <PlaceholderSection title="Roles & Permissions" description="Define what each role level can access across the platform" icon={Lock} />;
      case 'lanes': return <PlaceholderSection title="Cognitive Lanes" description="Customize lane names, colors, and default assignments" icon={Layers} />;
      case 'webhooks': return <PlaceholderSection title="Webhooks" description="Configure webhook endpoints for event automation" icon={Zap} />;
      case 'dashboard': return <PlaceholderSection title="Dashboard Layout" description="Customize which sections appear on your Pulse and Focus views" icon={Layers} />;
      case 'language': return <PlaceholderSection title="Language & Region" description="Set your preferred language and regional formats" icon={Globe} />;
      case 'visibility': return <PlaceholderSection title="Framework Visibility" description="Control which team members can see cognitive load data" icon={Eye} />;
      default: return <ProfileSection />;
    }
  };
  
  // Get current section info for header
  const getCurrentSectionInfo = () => {
    for (const [_, group] of Object.entries(SETTINGS_CATEGORIES)) {
      const item = group.items.find(i => i.id === activeSection);
      if (item) return item;
    }
    return { label: 'Settings', description: 'Configure your workspace' };
  };
  
  const currentSection = getCurrentSectionInfo();
  
  return (
    <div className="flex gap-8 min-h-[calc(100vh-200px)]" data-testid="settings-page">
      <SettingsNav activeSection={activeSection} onSelect={setActiveSection} />
      
      <div className="flex-1 min-w-0">
        {/* Section Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-black" style={{ color: DESIGN.colors.textPrimary }}>
            {currentSection.label}
          </h1>
          {currentSection.description && (
            <p className="text-sm mt-1" style={{ color: DESIGN.colors.textMuted }}>
              {currentSection.description}
            </p>
          )}
        </div>
        
        {renderContent()}
      </div>
      
      {/* Custom scrollbar styles */}
      <style>{`
        .settings-scroll::-webkit-scrollbar {
          width: 6px;
        }
        .settings-scroll::-webkit-scrollbar-track {
          background: transparent;
        }
        .settings-scroll::-webkit-scrollbar-thumb {
          background: rgba(255,255,255,0.1);
          border-radius: 3px;
        }
        .settings-scroll::-webkit-scrollbar-thumb:hover {
          background: rgba(255,255,255,0.2);
        }
        
        input[type="range"]::-webkit-slider-thumb {
          appearance: none;
          width: 24px;
          height: 24px;
          border-radius: 50%;
          background: white;
          cursor: pointer;
          box-shadow: 0 2px 8px rgba(0,0,0,0.15);
          border: 3px solid currentColor;
        }
      `}</style>
    </div>
  );
};

export default Settings;
